import React, { useMemo, useState } from 'react';
import { COLORS, SpinnerBlock, ErrorBlock, EmptyBlock } from './InventoryUIKit';

/**
 * InvoiceTable
 *
 * Generic, reusable data table used across Purchase Batches and the
 * Monthly / Camp / Bag invoice screens. Supports sticky header, client-side
 * sorting, and an optional grand-total footer row.
 *
 * @param {Array<{key, label, align?, sortable?, render?}>} columns
 * @param {Array<Object>} rows
 * @param {boolean} loading
 * @param {string} error
 * @param {() => void} [onRetry]
 * @param {string} [emptyMessage]
 * @param {React.ReactNode} [footer] - extra <tr> elements (e.g. Grand Total row)
 * @param {string} [rowKey] - field name to use as React key (default 'id')
 */
const InvoiceTable = ({
  columns,
  rows,
  loading,
  error,
  onRetry,
  emptyMessage = 'No records found.',
  footer = null,
  rowKey = 'id',
}) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av ?? '').localeCompare(String(bv ?? ''))
        : String(bv ?? '').localeCompare(String(av ?? ''));
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  };

  if (loading) return <SpinnerBlock />;
  if (error) return <ErrorBlock message={error} onRetry={onRetry} />;
  if (!rows || rows.length === 0) return <EmptyBlock message={emptyMessage} />;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ backgroundColor: COLORS.lightGrey, position: 'sticky', top: 0, zIndex: 1 }}>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col)}
                style={{
                  padding: '10px 12px',
                  textAlign: col.align || 'left',
                  color: COLORS.mediumText,
                  fontWeight: '600',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  cursor: col.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                }}
              >
                {col.label}
                {col.sortable && sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => (
            <tr key={row[rowKey] ?? idx} style={{ borderTop: `1px solid ${COLORS.lightGrey}` }}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: '10px 12px',
                    textAlign: col.align || 'left',
                    color: COLORS.darkText,
                    verticalAlign: 'middle',
                  }}
                >
                  {col.render ? col.render(row, idx) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
};

export default InvoiceTable;
